import { Auth0ContextInterface, User } from '@auth0/auth0-react';
import {
  InMemoryCache, Server, SessionManager, UnauthenticatedReason,
} from '@ynomia/client';
import {
  createRejectedTokenTracker,
  installCoordinatedRefresh,
  observeRejectedTokens,
} from '@ynomia/client-auth';
import type { Dispatch } from 'react';
import {
  ACCESS_TOKEN_STORAGE_KEY,
  ID_TOKEN_STORAGE_KEY,
  REFRESH_TOKEN_STORAGE_KEY,
  YNOMIA_CLIENT_SDK_NAME,
} from '../../config/constants';
import { isPublicPath } from '../../utils/url';
import { ClientCacheAction } from '../../context';
import authService from './Auth';
import { bootstrap } from '..';
import cacheObserver from './CacheObserver';
import config from '../../config';

const BOOTSTRAP_INTERVAL_MS = 1000 * 60 * 60;
const SESSION_RETRY_MS = 1000 * 10;
const SESSION_RETRY_MAX_MS = 1000 * 60 * 5;

/**
 * This service abstracts the private npm package, '@ynomia/client'. For further
 * documentation, please visit: https://www.npmjs.com/package/@ynomia/client
 *
 * You use this library to make API requests to the core Ynomia backend services.
 */
export default class YnomiaClient {
  private clientCache: InMemoryCache;

  private clientSession: SessionManager;

  private clientServer: Server;

  private sessionStarted = false;

  private sessionFailures = 0;

  private sessionRetryTimer?: ReturnType<typeof setTimeout>;

  constructor() {
    this.clientCache = new InMemoryCache();
    this.clientSession = new SessionManager(this.clientCache);
    this.clientServer = new Server(this.session, this.clientCache);
  }

  /**
   * Initializes the Ynomia Client with a fresh state. This method should be called
   * shortly after the web application first starts up.
   * @return {void}
   */
  async initialize(
    auth0: Auth0ContextInterface<User>,
    projectId: string | undefined,
    clientCacheDispatch: Dispatch<ClientCacheAction>,
  ): Promise<void> {
    const accessToken = localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
    const idToken = localStorage.getItem(ID_TOKEN_STORAGE_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
    this.clientCache = new InMemoryCache({
      clientName: YNOMIA_CLIENT_SDK_NAME,
      platform: 'web',
      environment: config.environment,
      baseURL: config.host.api,
      auth: {
        accessToken: (accessToken as string),
        idToken: (idToken as string),
        refreshToken: (refreshToken as string),
      },
      projectId,
    });
    this.clientSession = new SessionManager(this.clientCache);
    // Every app on this origin (Web HQ tabs, and the apps mounted beside it, standalone or in
    // the Embed Viewer) shares one token set in local storage and one Auth0 refresh token
    // family. Routing each refresh, including the SDK's own refresh on a 401, through
    // @ynomia/client-auth's origin-wide lock makes a realm that missed a rotation adopt the
    // stored tokens instead of presenting a superseded refresh token, which would revoke the
    // family for every app. This must wrap the session before the Server captures it.
    const rejectedTokens = createRejectedTokenTracker();
    installCoordinatedRefresh(this.clientSession, this.clientCache, { rejectedTokens });
    this.clientServer = new Server(this.session, this.clientCache);
    observeRejectedTokens(this.clientServer, rejectedTokens);

    // Subscribe our own event listeners to Client SDK Observables
    cacheObserver.initialize({ onChange$: this.clientCache.onChange$ }, clientCacheDispatch);
    authService.initialize(
      {
        onChange$: this.clientCache.onChange$,
        onEstablish$: this.clientSession.onEstablish$,
        onRefresh$: this.clientSession.onRefresh$,
        onRefreshFailure$: this.clientSession.onRefreshFailure$,
        onTerminate$: this.clientSession.onTerminate$,
      },
      auth0,
    );

    await this.ensureSession(projectId);
  }

  /**
   * Loads the user's project data and starts the hourly refresh of it. Does nothing until the
   * user is authenticated, or on a public page, and runs at most once.
   * @param {string?} projectId
   * @return {Promise<void>}
   */
  async ensureSession(projectId?: string): Promise<void> {
    // Public pages must not send authenticated requests: a stale token there ends in a logout
    // that clears the session of every app on this origin. The session starts on the next page
    // that needs it instead (see SessionStarter).
    if (
      this.sessionStarted
      || isPublicPath(window.location.pathname)
      || !this.clientCache.current.auth.isAuthenticated
    ) return;
    this.sessionStarted = true;
    clearTimeout(this.sessionRetryTimer);

    try {
      await bootstrap(projectId);
    } catch (error) {
      // Try again rather than leave the page empty: soon if another app on this origin held the
      // refresh lock for too long (e.g. while its own refresh kept retrying through an outage),
      // and with a growing delay after anything else, likely a backend outage.
      // @todo Add some logic to display a page which explains we couldn't connect to
      // Ynomia at this time and that you should try refreshing the page soon. Session
      // refresh issues will not get here - logout logic is handled elsewhere.
      this.sessionStarted = false;
      let delay = SESSION_RETRY_MS;
      if (!(error as { retryable?: boolean } | undefined)?.retryable) {
        delay = Math.min(SESSION_RETRY_MS * 2 ** this.sessionFailures, SESSION_RETRY_MAX_MS);
        this.sessionFailures += 1;
      }
      this.sessionRetryTimer = setTimeout(() => this.ensureSession(projectId), delay);
      return;
    }
    this.sessionFailures = 0;

    // Refetch bootstrap every hour to ensure the client is up-to-date.
    setInterval(
      () => {
        // Skip it after an in-app move to a public page, or once signed out
        if (
          isPublicPath(window.location.pathname)
          || !this.clientCache.current.auth.isAuthenticated
        ) return;
        // Use clientCache projectId as the input param can be undefined if
        // the url didn't initially specify the project.
        this.server.bootstrap(this.clientCache.current.projectId ?? undefined);
      },
      BOOTSTRAP_INTERVAL_MS,
    );
  }

  /**
   * Sets the Base URL that we should use based on the user's region and environment selections.
   * @return {void}
   */
  configureBaseURL(): void {
    this.clientCache.assign({ baseURL: config.host.api });
  }

  /**
   * @return {InMemoryCache}
   */
  get cache(): InMemoryCache {
    return this.clientCache;
  }

  /**
   * @return {SessionManager}
   */
  get session(): SessionManager {
    return this.clientSession;
  }

  /**
   * @return {Server}
   */
  get server(): Server {
    return this.clientServer;
  }

  /**
   * Clears an existing user's session from local storage and the application memory.
   * @return {Promise<void>}
   */
  async logout(reason?: UnauthenticatedReason): Promise<void> {
    this.clientSession.destroy(reason);
  }
}
